import { create } from "zustand";

import type { WalletTier } from "../api/types";

/**
 * Квалификация кошельков: адрес → тир.
 *
 * # Почему отдельный стор, а не поле строки ленты
 *
 * Незнакомый кошелёк — ровно тот случай, ради которого веха M6 и сделана. Он
 * приезжает в конверте сделки БЕЗ тира, потому что бэкфилл ещё не дошёл, и
 * получает квалификацию секундами позже сообщением profile_update. Прикрепи мы
 * тир к строке ленты в момент прихода — строка навсегда осталась бы без него до
 * перезагрузки вкладки, то есть именно там, где он нужнее всего.
 *
 * Строка ленты поэтому смотрит тир ПО АДРЕСУ, а стор питают два источника:
 * counterparty_tiers конверта сделки (мгновенно, для знакомых) и profile_update
 * канала md.profiles (задним числом, для незнакомых).
 *
 * # Потолок
 *
 * Лента держит 1000 сделок, у каждой единицы контрагентов. Потолок вдвое выше
 * ленты: адрес, вытесненный из стора раньше, чем его строка исчезнет из ленты,
 * дал бы «неизвестен» напротив сделки, тир которой мы только что знали.
 */
const TIERS_LIMIT = 4000;

interface TiersState {
  tiers: Record<string, WalletTier>;
  /** Тиры из конверта сделки: пачкой, потому что контрагентов бывает несколько. */
  ingest: (tiers: Record<string, WalletTier> | undefined) => void;
  /** Тир из profile_update: он и дорисовывает «неизвестен» задним числом. */
  set: (address: string, tier: WalletTier) => void;
  clear: () => void;
}

/** Порядок появления адресов — для вытеснения самых давних. */
let order: string[] = [];

function remember(next: Record<string, WalletTier>, address: string): void {
  if (!(address in next)) order.push(address);
}

function prune(next: Record<string, WalletTier>): Record<string, WalletTier> {
  if (order.length <= TIERS_LIMIT) return next;
  const drop = order.length - TIERS_LIMIT;
  for (const address of order.slice(0, drop)) delete next[address];
  order = order.slice(drop);
  return next;
}

export const useTiers = create<TiersState>((setState) => ({
  tiers: {},

  ingest: (incoming) => {
    if (!incoming) return;
    const entries = Object.entries(incoming);
    if (entries.length === 0) return;

    setState((state) => {
      // Отсечка холостого ре-рендера: web_api отдаёт 200 последних событий
      // ленты при КАЖДОМ подключении, и без неё переподключение переписывало бы
      // стор двумястами одинаковых пачек.
      if (entries.every(([address, tier]) => state.tiers[address] === tier)) {
        return state;
      }
      const next = { ...state.tiers };
      for (const [address, tier] of entries) {
        remember(next, address);
        next[address] = tier;
      }
      return { tiers: prune(next) };
    });
  },

  set: (address, tier) => {
    if (!address) return;
    setState((state) => {
      if (state.tiers[address] === tier) return state;
      const next = { ...state.tiers };
      remember(next, address);
      next[address] = tier;
      return { tiers: prune(next) };
    });
  },

  clear: () => {
    order = [];
    setState({ tiers: {} });
  },
}));
